import { useEffect, useState } from 'react'
import { COLORS } from '../../lib/theme'

/**
 * Geometry lifted from the marketing header on wehunt.app so the signed-in
 * shell and the public site read as one bar. The site ships these as CSS
 * module rules; the numbers below are the same values, kept in one place
 * because five components in this folder have to agree on them.
 *
 * The mobile column applies below `MOBILE_MAX` — the compact pill: tighter
 * padding, smaller logo and icons.
 *
 * This is NOT the width at which the account rail becomes a drawer; that is
 * `NAV_DRAWER_MAX` in lib/useNavDrawer, and it is wider. The two used to be
 * one number, but every iPad in portrait needs the drawer while still having
 * room for the roomy pill, so they were split.
 */
export const MOBILE_MAX = 768

export const PILL = {
  /** wehunt.app caps its pill here and centres it over the page. */
  maxWidth: 716,
  height: 56,
  heightMobile: 48,
  radius: 100,
  padding: '8px 24px',
  paddingMobile: '8px 12px',
  gap: 16,
  gapMobile: 8,
  leftGap: 24,
  leftGapMobile: 12,
  logoHeight: 32,
  logoHeightMobile: 24,
} as const

export const ICON_BTN = { size: 34, sizeMobile: 30, icon: 24, iconMobile: 20 } as const

/** Backdrop shared by the menu, search and location surfaces. */
export const OVERLAY_STYLE = {
  position: 'fixed',
  inset: 0,
  zIndex: 1000,
  background: 'rgba(17, 17, 17, 0.75)',
  backdropFilter: 'blur(3.5px)',
  border: 'none',
  padding: 0,
  cursor: 'default',
} as const

/**
 * The pill sits above the backdrop and the panels above the pill — the site
 * keeps the bar readable while a panel is open rather than dimming it.
 */
export const Z = { overlay: 1000, pill: 1010, panel: 1020 } as const

/**
 * Where the menu and search surfaces hang. Both are the site's own absolute
 * offsets: its pill also occupies 16..72, and it drops both panels at 104 on a
 * desktop and 80 on a phone.
 */
export const PANEL_TOP = 104
export const PANEL_TOP_MOBILE = 80

/**
 * The card wehunt.app shares between its menu and its search surface — the menu
 * is this card alone, search is the same card with a field above the columns.
 */
export const MENU_CARD = {
  /** The panel cap; wider than the 716px pill it hangs from. */
  maxWidth: 1123,
  /** Search hangs off a narrower panel, matching the pill exactly. */
  searchMaxWidth: 716,
  padding: 40,
  paddingMobile: 24,
  radius: 24,
  /** Between the link columns, and again as each column's own left inset. */
  columnGap: 56,
  /** Between a column's heading and its links. */
  columnStackGap: 24,
  linkGap: 16,
  titleSize: 24,
  linkSize: 16,
} as const

/** The hairline on the menu card; defined in styles/tokens.css for both themes. */
export const MENU_DIVIDER = 'var(--wh-menu-divider)'

export function iconButtonStyle(mobile: boolean) {
  const size = mobile ? ICON_BTN.sizeMobile : ICON_BTN.size
  return {
    position: 'relative' as const,
    width: size,
    height: size,
    flexShrink: 0,
    borderRadius: 999,
    background: 'transparent',
    // A *fixed* ink: the pill's `secondary` background is a mid green in light
    // and a light sage in dark, and `onSecondaryContainer` flips to near-white
    // in dark — which would leave pale icons on a pale pill. `onSecondaryFixed`
    // is #2a2f1f in both themes, matching the dark ink wehunt.app uses.
    color: COLORS.onSecondaryFixed,
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'background-color 0.2s',
  }
}

/**
 * Whether a menu link stays inside the app. Only such links are handed to the
 * shell's router; an absolute URL (the shop, a store badge) is left to the
 * browser, which is the only thing that can leave the origin.
 */
export function isInAppHref(href: string): boolean {
  return href.startsWith('/') && !href.startsWith('//')
}

/** Calls `onClose` on Escape while `open`. */
export function useCloseOnEscape(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])
}

/**
 * Tracks the viewport against `MOBILE_MAX`. The pill needs this in JS, not only
 * in CSS, because the menu toggle changes *what it opens* at that width — a
 * stylesheet can restyle a control but cannot rebind it.
 *
 * Both `change` and `resize` are observed, and each re-reads `matches` rather
 * than trusting an event payload. A MediaQueryList `change` alone is not
 * enough: under device emulation the viewport can cross the breakpoint without
 * one firing, which strands the JS sizing on desktop while the stylesheet has
 * already gone mobile — a visible desync, since the two style the same card.
 *
 * `matchMedia` is feature-detected rather than assumed: it is absent under SSR
 * and in the jsdom build the component tests run on, and the bar has to render
 * there (falling back to the desktop layout) instead of throwing.
 */
export function useIsMobileTopBar() {
  const query = `(max-width: ${MOBILE_MAX}px)`
  const [mobile, setMobile] = useState(() => matchMobile(query))
  useEffect(() => {
    const mq = typeof window !== 'undefined' ? window.matchMedia?.(query) : undefined
    if (!mq) return
    const sync = () => setMobile(mq.matches)
    sync()
    window.addEventListener('resize', sync)
    // Safari below 14 only has the deprecated listener pair.
    if (mq.addEventListener) mq.addEventListener('change', sync)
    else mq.addListener(sync)
    return () => {
      window.removeEventListener('resize', sync)
      if (mq.removeEventListener) mq.removeEventListener('change', sync)
      else mq.removeListener(sync)
    }
  }, [query])
  return mobile
}

function matchMobile(query: string) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia(query).matches
}
